import { quat, vec3 } from 'gl-matrix';
import type { Scene } from '../scene/Scene';
import { resolveSocket } from './socketResolver';
import type {
  AmbientSoundSlotDef,
  FlameSlotDef,
  LightSlotDef,
  MeshSlotDef,
  SceneCompositeInstance,
} from './types';

/**
 * Composition rules for placing a slot's child inside a composite — shared
 * between `lightEmitter`'s construction path and `CompositeInstance.applyTransform`'s
 * live-drag recomputation. Keeping this in one place removes the prior
 * "keep these in sync" comment and the latent bug where `applyTransform`
 * ignored anchor sockets on the light slot.
 *
 * Rules:
 *   childPosition = placementPosition + (placementRotation × localPosition) × placementScale
 *   childRotation = placementRotation × localRotation
 *   childScale    = placementScale × localScale
 *
 * For the light slot, `localPosition` / `localRotation` are overridden by the
 * named socket's glTF node TRS when `anchorSocket` resolves to an actual node.
 */

export interface ComposedTRS {
  position: [number, number, number];
  rotation: quat;
  scale: number;
}

/** Compose the world TRS for a mesh slot. */
export function composeMeshWorldTRS(
  placement: SceneCompositeInstance,
  meshSlot: MeshSlotDef,
): ComposedTRS {
  const localPos = meshSlot.localPosition ?? [0, 0, 0];
  const localRot = meshSlot.localRotation ?? [0, 0, 0, 1];
  const localScale = meshSlot.localScale ?? 1;
  return composeFromLocal(placement, localPos, localRot, localScale);
}

/**
 * Compose the world position + rotation for the light slot. `anchorSocket` on
 * the light slot, when set, names a glTF empty inside the mesh-slot's GLB —
 * its node TRS overrides localPosition/localRotation.
 */
export function composeLightWorldPosition(
  placement: SceneCompositeInstance,
  lightSlot: LightSlotDef,
  meshSlot: MeshSlotDef,
  scene: Scene,
): { position: [number, number, number]; rotation: quat } {
  const local = effectiveLightLocal(lightSlot, meshSlot, scene);
  const composed = composeFromLocal(placement, local.position, local.rotation, 1);
  return { position: composed.position, rotation: composed.rotation };
}

/**
 * Resolve the light slot's effective local TRS, applying anchor-socket lookup
 * when set. Returns plain tuples — callers compose with the placement.
 *
 * Exposed so the editor preview pane can share the same lookup rules.
 */
export function effectiveLightLocal(
  lightSlot: LightSlotDef,
  meshSlot: MeshSlotDef,
  scene: Scene,
): { position: readonly [number, number, number]; rotation: readonly [number, number, number, number] } {
  if (lightSlot.anchorSocket && meshSlot.modelId) {
    const model = scene.modelRegistry.get(meshSlot.modelId);
    const socket = model ? resolveSocket(model, lightSlot.anchorSocket) : null;
    if (socket) {
      return { position: socket.translation, rotation: socket.rotation };
    }
  }
  return {
    position: lightSlot.localPosition ?? [0, 0, 0],
    rotation: lightSlot.localRotation ?? [0, 0, 0, 1],
  };
}

/**
 * Compose the world position for the flame slot. `anchorSocket`, when set,
 * overrides `localPosition` with the named glTF empty's translation —
 * matches the LightSlot lookup pattern. The flame has no orientation (it
 * billboards toward the camera at draw time), so only position is composed.
 */
export function composeFlameWorldPosition(
  placement: SceneCompositeInstance,
  flameSlot: FlameSlotDef,
  meshSlot: MeshSlotDef,
  scene: Scene,
): [number, number, number] {
  let local: readonly [number, number, number] = flameSlot.localPosition ?? [0, 0, 0];
  if (flameSlot.anchorSocket && meshSlot.modelId) {
    const model = scene.modelRegistry.get(meshSlot.modelId);
    const socket = model ? resolveSocket(model, flameSlot.anchorSocket) : null;
    if (socket) local = socket.translation;
  }
  // Flames have no rotation contribution — pass identity for the local quat
  // so composeFromLocal still applies the placement scale + rotation.
  return composeFromLocal(placement, local, [0, 0, 0, 1], 1).position;
}

/**
 * Compose the ambient-sound's world position. v1 doesn't support an anchor
 * socket — the sound emanates from the composite root (or an authored local
 * offset). Adding socket support later mirrors `composeFlameWorldPosition`.
 */
export function composeAmbientSoundWorldPosition(
  placement: SceneCompositeInstance,
  ambientSlot: AmbientSoundSlotDef,
): [number, number, number] {
  return composeFromLocal(placement, ambientSlot.localPosition ?? [0, 0, 0], [0, 0, 0, 1], 1)
    .position;
}

function composeFromLocal(
  placement: SceneCompositeInstance,
  localPos: readonly [number, number, number],
  localRot: readonly [number, number, number, number],
  localScale: number,
): ComposedTRS {
  const worldRot = quat.fromValues(
    placement.rotation[0] as number,
    placement.rotation[1] as number,
    placement.rotation[2] as number,
    placement.rotation[3] as number,
  );
  const offset = vec3.transformQuat(
    vec3.create(),
    vec3.fromValues(localPos[0], localPos[1], localPos[2]),
    worldRot,
  );
  const placementScale = placement.scale;
  const position: [number, number, number] = [
    (placement.position[0] as number) + offset[0] * placementScale,
    (placement.position[1] as number) + offset[1] * placementScale,
    (placement.position[2] as number) + offset[2] * placementScale,
  ];
  const rotation = quat.multiply(
    quat.create(),
    worldRot,
    quat.fromValues(localRot[0], localRot[1], localRot[2], localRot[3]),
  );
  return { position, rotation, scale: placementScale * localScale };
}
